import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  descartarCompactacaoPonto, gravarCompactacaoPontos, gravarCompactacaoUmidades,
  verIscsDaCompactacao,
} from '../../api/compactacao';
import type { EnsaioCompactacao, IscDaCompactacao } from '../../api/compactacao';
import { verRepeticao } from '../../api/repeticao';
import { BlocoDaFicha, JanelaDeDescarte } from '../../Ficha';
import { numero, paraNumero, plural } from '../../formato';
import { useSessao } from '../../sessao';
import {
  Aviso, CampoNumerico, ErroDaApi, EscolhaPorCodigo, SeloDoEnsaio, TabelaDeLancamento, comoCodigo,
} from '../../ui';
import type { ColunaDeLancamento, LinhaDeLancamento, Opcao } from '../../ui';
import { CASAS, CurvaDeCompactacao } from '../Compactacao';
import { MolduraDaRepeticao, useRepeticao } from './Repeticao';
import type { Comparacao, UseRepeticao } from './Repeticao';
import { comparacaoNumerica } from './RepeticaoNumerica';
import type { ResultadoNumerico } from './RepeticaoNumerica';

/**
 * Repetir a compactação na Interpretação (onda 323, D7: as curvas).
 *
 * À esquerda, os pontos da repetição — o cilindro com o solo e a cápsula da
 * umidade de cada um, como na Pesagem, só que aqui; à direita, a curva
 * inicial tracejada sob a atual. No topo, a w ótima e a γd máx das duas
 * tentativas, do servidor (`vw_ensaio_compactacao` na vista de cada uma).
 * Embaixo, os ISCs moldados nesta compactação: adotar a repetição leva cada
 * um de volta a «Em execução» (RN-260), e um ISC aprovado faz o banco recusar
 * a adoção — a frase dele aparece na janela de adotar.
 */

// ══ a comparação ══════════════════════════════════════════════════════

const pontosValidos = (f: any) => (f?.pontos ?? []).filter((p: any) => !p.descartado).length;
const notaDaCurva = (f: any) => (f?.cabecalho?.curva_motivo
  ?? plural(pontosValidos(f), 'ponto válido', 'pontos válidos'));

/* Funções, e não constantes: `CASAS` mora em `Compactacao.tsx`, que importa
   este arquivo — lida na carga do módulo, ainda não existiria. */
const W_OTIMA = (): ResultadoNumerico => ({
  rotulo: 'w ótima', unidade: '%', casas: CASAS.wOtima, unidadeDaDiferenca: 'p.p.',
  valor: (f) => (f?.cabecalho?.umidade_otima_norma != null
    ? Number(f.cabecalho.umidade_otima_norma) : null),
  nota: (f) => notaDaCurva(f),
  semDiferenca: 'sai quando a curva da repetição fechar',
});
const GAMA_MAX = (): ResultadoNumerico => ({
  rotulo: 'γd máx', unidade: 'g/cm³', casas: CASAS.gamaMax,
  valor: (f) => (f?.cabecalho?.massa_especifica_seca_maxima_norma != null
    ? Number(f.cabecalho.massa_especifica_seca_maxima_norma) : null),
  semDiferenca: 'sai quando a curva da repetição fechar',
});

const comparacao = (i: EnsaioCompactacao, a: EnsaioCompactacao | null): Comparacao[] => [
  { ...comparacaoNumerica(W_OTIMA(), i, a), rotulo: 'w ótima', chave: 'w-otima' },
  { ...comparacaoNumerica(GAMA_MAX(), i, a), rotulo: 'γd máx', chave: 'gama-max' },
];

// ══ os pontos ═════════════════════════════════════════════════════════

const COLUNAS: ColunaDeLancamento[] = [
  { texto: 'Ponto' },
  { texto: 'Cilindro' },
  { texto: 'Cil. + solo (g)', num: true },
  { texto: 'Cápsula' },
  { texto: 'Úmida + cáp. (g)', num: true },
  { texto: 'Seca + cáp. (g)', num: true },
  { texto: 'w (%)', num: true },
  { texto: 'γd (g/cm³)', num: true },
];

/** Um ponto gravado, em leitura, com a(s) cápsula(s) dele. */
function linhaDoPonto(p: any, f: any): LinhaDeLancamento {
  const caps = (f?.umidades ?? []).filter((u: any) => u.ponto_id === p.id && !u.descartada);
  const u = caps.length === 1 ? caps[0] : null;
  const esperando = caps.some((x: any) => x.massa_capsula_solo_seco == null);
  const semUmidade = !p.descartado && caps.length === 0;
  return {
    chave: p.id,
    classe: p.descartado ? 'descartada' : esperando || semUmidade ? 'nova' : undefined,
    titulo: <>Ponto {p.ordem} · <span className="cod">{p.cilindro}</span></>,
    resultado: p.descartado ? '—' : p.massa_especifica_seca != null
      ? <span className="n">γd {numero(p.massa_especifica_seca, CASAS.gama)}</span> : undefined,
    nota: p.descartado ? `Descartado: ${p.motivo_descarte ?? ''}` : undefined,
    semCartao: [0, 7],
    celulas: [
      p.ordem,
      <span className="cod">{p.cilindro}</span>,
      numero(p.massa_cilindro_solo, 1),
      u ? <span className="cod">{u.capsula}</span>
        : caps.length ? plural(caps.length, 'cápsula', 'cápsulas') : '—',
      u ? numero(u.massa_capsula_solo_umido, 2) : '—',
      u?.massa_capsula_solo_seco != null ? numero(u.massa_capsula_solo_seco, 2) : '—',
      p.descartado ? 'descartado'
        : semUmidade ? <span className="sub">sem umidade</span>
        : esperando ? <span className="sub">espera a seca (na estufa)</span>
        : p.umidade_media != null ? numero(p.umidade_media, CASAS.wCapsula) : '—',
      p.massa_especifica_seca != null && !p.descartado
        ? <b className="fe-w">{numero(p.massa_especifica_seca, CASAS.gama)}</b> : '—',
    ],
  };
}

/** Os pontos de uma tentativa em leitura (a inicial, as anteriores). */
export function PontosDaCompactacaoEmLeitura({ f, rotulo }: { f: any; rotulo?: string }) {
  const pontos = [...(f?.pontos ?? [])].sort((a: any, b: any) => a.ordem - b.ordem);
  return (
    <TabelaDeLancamento rotulo={rotulo ?? 'Pontos da tentativa'} classe="fe-lanc repeticao-leitura"
      colunas={COLUNAS} linhas={pontos.map((p) => linhaDoPonto(p, f))} />
  );
}

// ══ os ISCs moldados nela ═════════════════════════════════════════════

const NAO_VOLTAM = new Set(['programado', 'aprovado', 'aguardando_verificacao']);

function IscsDaCompactacao({ iscs, numero: n }: { iscs: IscDaCompactacao[] | null; numero: number | null }) {
  if (!iscs?.length) return null;
  return (
    <BlocoDaFicha titulo="ISCs moldados nesta compactação"
      sub="Os corpos de prova deles são pontos da tentativa inicial. Adotar a repetição abre, em cada um, uma tentativa nova e vazia, com o mesmo motivo.">
      <div className="fe-miolo repeticao-iscs">
        {iscs.map((x) => (
          <Aviso key={x.id} tipo={x.status === 'aprovado' || x.status === 'aguardando_verificacao'
            || x.repeticao_aberta != null ? 'atencao' : 'info'}>
            <span className="repeticao-faixa">
              <span>
                <Link to={`/ensaio/${x.id}/isc`}>
                  <b>{x.metodo_codigo ?? 'ISC'} · amostra {x.amostra_codigo ?? x.amostra_identificacao ?? '—'}</b>
                </Link>{' '}
                foi moldado nesta compactação ({plural(x.corpos, 'corpo de prova', 'corpos de prova')}).{' '}
                {x.repeticao_aberta != null
                  ? `Este ISC tem uma repetição aberta (tentativa ${x.repeticao_aberta}): adote-a ou desista dela no ISC antes de adotar esta.`
                  : x.status === 'aprovado'
                  ? 'Está aprovado: enquanto estiver, o banco recusa a adoção da repetição.'
                  : x.status === 'aguardando_verificacao'
                    ? 'Está aguardando verificação: devolva antes de adotar.'
                    : x.status === 'programado'
                      ? 'Ainda programado: continua programado, sem corpo de prova.'
                      : <>Ao adotar, este ISC volta a “Em execução” para moldar os CPs na nova umidade
                        ótima{n != null ? ` da tentativa ${n}` : ''}.</>}
              </span>
              <SeloDoEnsaio status={x.status} />
            </span>
          </Aviso>
        ))}
        {iscs.some((x) => !NAO_VOLTAM.has(x.status)) && (
          <p className="sub">O motivo da adoção vai também para a trilha de cada ISC.</p>
        )}
      </div>
    </BlocoDaFicha>
  );
}

// ══ a tela ════════════════════════════════════════════════════════════

interface Rascunho { cil: string; massa: string; cap: string; umido: string; seco: string }
const vazio = (cil = ''): Rascunho => ({ cil, massa: '', cap: '', umido: '', seco: '' });
/** A cápsula de um ponto já gravado (o que ficou sem umidade). */
interface Capsula { cap: string; umido: string; seco: string }
const semCapsula = (): Capsula => ({ cap: '', umido: '', seco: '' });

export function RepeticaoCompactacao({
  id, congelado, opcoesCilindro, opcoesCapsula, versao, aoMudar, children,
}: {
  id: string;
  congelado: boolean;
  opcoesCilindro: Opcao[];
  opcoesCapsula: Opcao[];
  /** A versão da ficha: a lista dos ISCs relê com ela. */
  versao: number;
  aoMudar(): void | Promise<void>;
  /** A Interpretação de sempre (a curva do laudo). */
  children: ReactNode;
}) {
  const { pode } = useSessao();
  const podeInterpretar = pode('ensaio.interpretar');
  /* R1: com a repetição aberta, quem interpreta também lança nela. */
  const lanca = pode('ensaio.lancar') || podeInterpretar;
  const rep = useRepeticao<EnsaioCompactacao>(id, 'compactacao', aoMudar);
  const [rascunho, setRascunho] = useState<Rascunho | null>(null);
  const [mostrarInicial, setMostrarInicial] = useState(true);
  const [salvando, setSalvando] = useState<string | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [descartando, setDescartando] = useState<any | null>(null);
  const [iscs, setIscs] = useState<IscDaCompactacao[] | null>(null);
  /* A cápsula de cada ponto gravado sem umidade, pelo id do ponto: tentar de
     novo grava só a cápsula, nunca um segundo ponto. */
  const [capDe, setCapDe] = useState<Record<string, Capsula>>({});
  const cilindros = useMemo(() => opcoesCilindro.map(comoCodigo), [opcoesCilindro]);
  const capsulas = useMemo(() => opcoesCapsula.map(comoCodigo), [opcoesCapsula]);
  const aberta = rep.dados?.aberta?.numero ?? null;

  useEffect(() => {
    let vivo = true;
    verIscsDaCompactacao(id).then((x) => { if (vivo) setIscs(x); })
      .catch(() => { if (vivo) setIscs([]); });
    return () => { vivo = false; };
  }, [id, versao, aberta]);

  /* O cilindro do último ponto da repetição (ou da inicial) vem escolhido:
     quase sempre é o mesmo a noite inteira. */
  const ultimoCilindro = (r: UseRepeticao<EnsaioCompactacao>) => {
    const de = (f: any) => [...(f?.pontos ?? [])].reverse()
      .find((p: any) => !p.descartado)?.cilindro_equipamento_id;
    return de(r.dados?.atual) ?? de(r.dados?.inicial) ?? '';
  };
  const atualDe = (r: UseRepeticao<EnsaioCompactacao>) => rascunho ?? vazio(ultimoCilindro(r));
  const mexer = (r: UseRepeticao<EnsaioCompactacao>, c: keyof Rascunho, v: string) =>
    setRascunho((x) => ({ ...(x ?? vazio(ultimoCilindro(r))), [c]: v }));

  const estufaMudou = () => window.dispatchEvent(new CustomEvent('labmanager:estufa-mudou'));

  /** Grava a cápsula de um ponto que já existe (só a cápsula). */
  const gravarCapsula = async (pontoId: string, c: Capsula) => {
    const umido = paraNumero(c.umido);
    const seco = paraNumero(c.seco);
    await gravarCompactacaoUmidades(id, pontoId, {
      determinacoes: [{
        ordem: 1, capsulaEquipamentoId: c.cap, massaCapsulaSoloUmido: umido,
        ...(seco != null ? { massaCapsulaSoloSeco: seco } : {}),
      }],
    });
    setCapDe((m) => { const n = { ...m }; delete n[pontoId]; return n; });
    if (seco == null) estufaMudou();
  };

  const depoisDeGravar = async (r: UseRepeticao<EnsaioCompactacao>) => {
    setSalvando(null);
    await r.recarregar();
    void Promise.resolve(aoMudar()).catch(() => undefined);
  };

  const lancar = async (r: UseRepeticao<EnsaioCompactacao>) => {
    const x = atualDe(r);
    const massa = paraNumero(x.massa);
    if (!x.cil || massa == null || salvando) return;
    const ordem = (r.dados?.atual?.pontos ?? [])
      .reduce((m: number, p: any) => Math.max(m, Number(p.ordem)), 0) + 1;
    const cap: Capsula = { cap: x.cap, umido: x.umido, seco: x.seco };
    const comCapsula = !!x.cap && paraNumero(x.umido) != null;
    setSalvando('lancar'); setErro(null);
    try {
      /* O ponto e a cápsula pelas rotas de sempre: o banco põe os dois na
         repetição. A balança não vai — fica a do cabeçalho (R1). */
      await gravarCompactacaoPontos(id, {
        pontos: [{ ordem, cilindroEquipamentoId: x.cil, massaCilindroSolo: massa }],
      });
    } catch (e) { setErro(e); setSalvando(null); return; }
    /* Gravado o ponto, a linha nova se limpa já: tentar de novo nunca cria
       outro ponto. A cápsula, se houver, passa a ser do ponto gravado. */
    setRascunho(vazio(x.cil));
    try {
      if (comCapsula) {
        const lido = await verRepeticao(id, 'compactacao');
        const novo = (lido.atual?.pontos ?? []).find((p: any) => Number(p.ordem) === ordem);
        if (!novo) {
          throw new Error(`O ponto ${ordem} foi gravado, mas não voltou na releitura da repetição: `
            + 'lance a cápsula na linha dele, na tabela acima.');
        }
        setCapDe((m) => ({ ...m, [novo.id]: cap }));
        await gravarCapsula(novo.id, cap);
      }
    } catch (e) { setErro(e); }
    await depoisDeGravar(r);
  };

  const gravarCapsulaDoPonto = async (r: UseRepeticao<EnsaioCompactacao>, p: any) => {
    const c = capDe[p.id] ?? semCapsula();
    if (!c.cap || paraNumero(c.umido) == null || salvando) return;
    setSalvando(`cap-${p.id}`); setErro(null);
    try { await gravarCapsula(p.id, c); } catch (e) { setErro(e); }
    await depoisDeGravar(r);
  };

  const lancamento = (r: UseRepeticao<EnsaioCompactacao>) => {
    const atual = r.dados?.atual;
    const x = atualDe(r);
    const ordem = (atual?.pontos ?? []).reduce((m: number, p: any) => Math.max(m, Number(p.ordem)), 0) + 1;
    const falta = !x.cil ? 'Falta escolher o cilindro.'
      : paraNumero(x.massa) == null ? 'Falta a massa de cilindro + solo.'
        : x.cap && paraNumero(x.umido) == null ? 'Falta a massa de cápsula + solo úmido.'
          : null;
    const semUmidade = (p: any) => !p.descartado
      && !(atual?.umidades ?? []).some((u: any) => u.ponto_id === p.id && !u.descartada);
    const gravadas = [...(atual?.pontos ?? [])].sort((a: any, b: any) => a.ordem - b.ordem)
      .map((p: any) => {
        const l = linhaDoPonto(p, atual);
        const descartar = p.descartado || !lanca ? null : (
          <button type="button" className="botao discreto pequeno"
            aria-label={`Descartar o ponto ${p.ordem}`} disabled={salvando !== null}
            onClick={() => setDescartando(p)}>
            Descartar
          </button>
        );
        if (!lanca || !semUmidade(p)) return { ...l, celulas: [...l.celulas, descartar] };
        /* O ponto gravado sem umidade: a cápsula dele se lança nesta linha. */
        const c = capDe[p.id] ?? semCapsula();
        const mexerCap = (k: keyof Capsula, v: string) =>
          setCapDe((m) => ({ ...m, [p.id]: { ...(m[p.id] ?? semCapsula()), [k]: v } }));
        const faltaCap = !c.cap ? 'Falta escolher a cápsula.'
          : paraNumero(c.umido) == null ? 'Falta a massa de cápsula + solo úmido.' : null;
        const celulas = [...l.celulas];
        celulas[3] = <EscolhaPorCodigo rotulo={`Cápsula do ponto ${p.ordem}`} rotuloOculto
          opcoes={capsulas} valor={c.cap} aoEscolher={(v) => mexerCap('cap', v)} />;
        celulas[4] = <CampoNumerico rotulo={`Úmida + cápsula do ponto ${p.ordem} (g)`} rotuloOculto
          value={c.umido} onChange={(e) => mexerCap('umido', e.target.value)} />;
        celulas[5] = <CampoNumerico rotulo={`Seca + cápsula do ponto ${p.ordem} (g)`} rotuloOculto
          value={c.seco} placeholder="ou depois" onChange={(e) => mexerCap('seco', e.target.value)} />;
        return {
          ...l,
          nota: `Ponto ${p.ordem} gravado sem umidade: lance a cápsula nesta linha.`,
          celulas: [...celulas, (
            <span className="repeticao-seca">
              <button type="button" className="botao secundario pequeno" data-gravar-linha=""
                aria-disabled={faltaCap !== null || salvando !== null}
                title={faltaCap ?? `Grava a cápsula do ponto ${p.ordem}`}
                onClick={() => { if (!faltaCap && salvando === null) void gravarCapsulaDoPonto(r, p); }}>
                {salvando === `cap-${p.id}` ? 'Gravando…' : 'Gravar a umidade'}
              </button>
              {descartar}
            </span>
          )],
        };
      });
    const pendentes = (atual?.pontos ?? []).filter(semUmidade);
    const nova: LinhaDeLancamento | null = lanca ? {
      chave: 'nova',
      classe: 'nova',
      titulo: `Ponto ${ordem} · novo`,
      semCartao: [0, 6, 7],
      celulas: [
        <span className="sub">{ordem}</span>,
        <EscolhaPorCodigo rotulo="Cilindro do ponto novo" rotuloOculto opcoes={cilindros}
          valor={x.cil} aoEscolher={(v) => mexer(r, 'cil', v)} />,
        <CampoNumerico rotulo="Cilindro + solo do ponto novo (g)" rotuloOculto
          value={x.massa} onChange={(e) => mexer(r, 'massa', e.target.value)} />,
        <EscolhaPorCodigo rotulo="Cápsula do ponto novo" rotuloOculto opcoes={capsulas}
          valor={x.cap} aoEscolher={(v) => mexer(r, 'cap', v)} />,
        <CampoNumerico rotulo="Úmida + cápsula do ponto novo (g)" rotuloOculto
          value={x.umido} onChange={(e) => mexer(r, 'umido', e.target.value)} />,
        <CampoNumerico rotulo="Seca + cápsula do ponto novo (g)" rotuloOculto
          value={x.seco} placeholder="ou depois"
          onChange={(e) => mexer(r, 'seco', e.target.value)} />,
        <span className="sub">—</span>,
        <span className="sub">—</span>,
        <button type="button" className="botao secundario pequeno fe-gravar-linha" data-gravar-linha=""
          aria-disabled={falta !== null || salvando !== null}
          title={falta ?? 'Grava o ponto na repetição (Enter)'}
          onClick={() => { if (!falta && salvando === null) void lancar(r); }}>
          {salvando === 'lancar' ? 'Gravando…' : 'Gravar ponto'}
        </button>,
      ],
    } : null;
    return (
      <BlocoDaFicha titulo={`Pontos novos · tentativa ${r.dados?.aberta?.numero ?? ''}`}
        sub="Massa do cilindro + solo e a cápsula da umidade de cada ponto, como na Pesagem — só que aqui. Volume e massa do cilindro vêm da calibração interna.">
        <div className="fe-tabela">
          <TabelaDeLancamento rotulo="Pontos da repetição — lançados e o novo"
            classe="fe-lanc repeticao-lanc" colunas={[...COLUNAS, { texto: '' }]}
            linhas={nova ? [...gravadas, nova] : gravadas} />
          {lanca && pendentes.length > 0 && (
            <p className="o-que-falta">
              {pendentes.length === 1
                ? `O ponto ${pendentes[0].ordem} está sem umidade: lance a cápsula na linha dele.`
                : `Os pontos ${pendentes.map((p: any) => p.ordem).join(', ')} estão sem umidade: lance a cápsula na linha de cada um.`}
            </p>
          )}
          <p className="sub repeticao-dica">
            {lanca
              ? 'Sem a seca, a cápsula espera a estufa (a seca também entra pela Bancada › Estufa); outra cápsula do mesmo ponto se lança na etapa Pontos.'
              : 'Lançar pede a permissão de lançar dados de ensaio: os pontos chegam aqui quando o laboratório os lança na etapa Pontos.'}
          </p>
          <ErroDaApi erro={erro} />
          <JanelaDeDescarte
            alvo={descartando ? `o ponto ${descartando.ordem} · ${descartando.cilindro ?? ''}` : null}
            aoFechar={() => setDescartando(null)}
            aoConfirmar={async (motivo) => {
              setSalvando('descarte'); setErro(null);
              try {
                await descartarCompactacaoPonto(id, descartando.id, motivo);
                setSalvando(null);
                await r.recarregar();
                void Promise.resolve(aoMudar()).catch(() => undefined);
                return true;
              } catch (e) { setErro(e); return false; } finally { setSalvando(null); }
            }}
            salvando={salvando !== null} />
        </div>
      </BlocoDaFicha>
    );
  };

  const grafico = (r: UseRepeticao<EnsaioCompactacao>) => (r.dados?.atual ? (
    <CurvaDeCompactacao d={r.dados.atual} inicial={mostrarInicial ? r.dados.inicial : null}
      titulo="Curva de compactação · inicial × atual"
      acao={(
        <label className="marcavel">
          <input type="checkbox" checked={mostrarInicial}
            onChange={(e) => setMostrarInicial(e.target.checked)} />
          mostrar a inicial
        </label>
      )} />
  ) : null);

  /* A curva vem embaixo do lançamento, na largura toda: a linha nova tem
     cinco campos e não cabe ao lado do gráfico. */
  const depois = (r: UseRepeticao<EnsaioCompactacao>) => (
    <>
      {grafico(r)}
      <BlocoDaFicha titulo={`Pontos iniciais · tentativa ${r.dados?.vigente ?? ''}`}
        sub="Em leitura durante a repetição. Se você adotar a nova, saem da conta e do laudo e ficam no histórico.">
        <div className="fe-tabela">
          <PontosDaCompactacaoEmLeitura f={r.dados?.inicial} rotulo="Pontos iniciais" />
        </div>
      </BlocoDaFicha>
      <IscsDaCompactacao iscs={iscs} numero={r.dados?.aberta?.numero ?? null} />
    </>
  );

  return (
    <MolduraDaRepeticao ensaioId={id} ficha="compactacao" titulo="Compactação"
      podeInterpretar={podeInterpretar} congelado={congelado} repeticao={rep} aoMudar={aoMudar}
      frase={<>Os pontos novos entram no gráfico assim que a umidade da cápsula sai; até lá,
        aparecem como «espera a seca». O laudo continua com a curva inicial até você adotar a
        repetição.</>}
      comparacao={comparacao} lancamento={lancamento} depois={depois}>
      {children}
      <IscsDaCompactacao iscs={iscs} numero={null} />
    </MolduraDaRepeticao>
  );
}
